import * as React from "react";
import { useState } from "react";
import { MenuItem, TextField } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { useTranslation } from "react-i18next";
import { ID_CIT, ID_MIT, ID_PRE_AUTHORIZED } from "../constants/PaymentsTypes";
import { ID_ONLINE, ID_POS } from "../constants/PaymentsModes";
import * as Yup from "yup";
import { useSetRecoilState } from "recoil";
import { STATE_ACTIVE_FILTERS } from "../constants/PaymentsRecoil";
import { addFilter } from "../../../utils/FilterUtils";
import {
    ID_FILTER_AMOUNTMODIFIED,
    ID_FILTER_CREATIONDATEFROM,
    ID_FILTER_CREATIONDATETO,
    ID_FILTER_MODE,
    ID_FILTER_MODIFICATIONDATEFROM,
    ID_FILTER_MODIFICATIONDATETO,
    ID_FILTER_SEARCH,
    ID_FILTER_TYPE,
} from "../constants/PaymentsFilters";
import AdvancedFilter from "../../../component/AdvancedFilter";
import { asEndOfDay, asStartOfDay } from "../../../utils/FinanceDateUtils";

function PaymentsFilter() {
    const { t } = useTranslation("finance", { keyPrefix: "finance" });
    const [search, setSearch] = useState("");
    const setFilters = useSetRecoilState(STATE_ACTIVE_FILTERS);

    const schema = Yup.object().shape({
        type: Yup.string(),
        mode: Yup.string(),
        amountModified: Yup.string(),
        creationDateFrom: Yup.date()
                .nullable()
                .test("testFrom", t("filters.errors.invalid-date"), (value, context) => {
                    return !value || !context.parent.creationDateTo || value <= context.parent.creationDateTo;
                })
                .typeError(t("filters.errors.invalid-date")),
        creationDateTo: Yup.date()
                .nullable()
                .test("testTo", t("filters.errors.invalid-date"), (value, context) => {
                    return !value || !context.parent.creationDateFrom || value >= context.parent.creationDateFrom;
                })
                .typeError(t("filters.errors.invalid-date")),
        modificationDateFrom: Yup.date()
                .nullable()
                .test("testFrom", t("filters.errors.invalid-date"), (value, context) => {
                    return !value || !context.parent.modificationDateTo || value <= context.parent.modificationDateTo;
                })
                .typeError(t("filters.errors.invalid-date")),
        modificationDateTo: Yup.date()
                .nullable()
                .test("testTo", t("filters.errors.invalid-date"), (value, context) => {
                    return !value || !context.parent.modificationDateFrom || value >= context.parent.modificationDateFrom;
                })
                .typeError(t("filters.errors.invalid-date")),
    });

    const defaultValues = {
        type: "",
        mode: "",
        amountModified: "",
        creationDateFrom: null,
        creationDateTo: null,
        modificationDateFrom: null,
        modificationDateTo: null,
    };

    const types = [
        {
            value: ID_CIT,
            label: t("filters.payments.types." + ID_CIT),
        },
        {
            value: ID_MIT,
            label: t("filters.payments.types." + ID_MIT),
        },
        {
            value: ID_PRE_AUTHORIZED,
            label: t("filters.payments.types." + ID_PRE_AUTHORIZED),
        },
    ];

    const modes = [
        {
            value: ID_ONLINE,
            label: t("filters.payments.modes." + ID_ONLINE),
        },
        {
            value: ID_POS,
            label: t("filters.payments.modes." + ID_POS),
        },
    ];

    const amountModified = [
        {
            value: true,
            label: t("filters.payments.amount-modified-true"),
        },
        {
            value: false,
            label: t("filters.payments.amount-modified-false"),
        },
    ];

    const renderType = (field: any) => (
            <TextField fullWidth name="type" select label={t("filters.payments.type")} value={field.type} onChange={field.onChange}>
                <MenuItem value={""}>
                    <em/>
                </MenuItem>
                {types.map((option: any) => (
                        <MenuItem key={option.value} value={option.value}>
                            {option.label}
                        </MenuItem>
                ))}
            </TextField>
    );

    const renderMode = (field: any) => (
            <TextField fullWidth name="mode" select label={t("filters.payments.mode")} value={field.mode} onChange={field.onChange}>
                <MenuItem value={""}>
                    <em/>
                </MenuItem>
                {modes.map((option: any) => (
                        <MenuItem key={option.value} value={option.value}>
                            {option.label}
                        </MenuItem>
                ))}
            </TextField>
    )

    const renderAmountModified = (field: any) => (
            <TextField fullWidth name="amountModified" select label={t("filters.payments.amount-modified")} value={field.amountModified} onChange={field.onChange}>
                <MenuItem value={""}>
                    <em/>
                </MenuItem>
                {amountModified.map((option: any) => (
                        <MenuItem key={option.value} value={option.value}>
                            {option.label}
                        </MenuItem>
                ))}
            </TextField>
    );

    const renderCreationDateFrom = (field: any, error: any, state: any, values: any) => (
            <DatePicker
                    views={["day"]}
                    label={t("filters.payments.label-date-from")}
                    value={field.value}
                    maxDate={values["creationDateTo"] !== null ? values["creationDateTo"] : null}
                    onChange={field.onChange}
                    renderInput={(params) => <TextField {...params} fullWidth id={"creationDateFrom"} error={error !== undefined} helperText={error ? error.message : ""}/>}
                    inputFormat={"L"}
            />
    );

    const renderCreationDateTo = (field: any, error: any, state: any, values: any) => (
            <DatePicker
                    views={["day"]}
                    label={t("filters.payments.label-date-to")}
                    value={field.value}
                    minDate={values["creationDateFrom"] !== null ? values["creationDateFrom"] : null}
                    onChange={field.onChange}
                    renderInput={(params) => <TextField {...params} fullWidth id={"creationDateTo"} error={error !== undefined} helperText={error ? error.message : ""}/>}
                    inputFormat={"L"}
            />
    );

    const renderModificationDateFrom = (field: any, error: any, state: any, values: any) => (
            <DatePicker
                    views={["day"]}
                    label={t("filters.payments.label-date-from")}
                    value={field.value}
                    maxDate={values["modificationDateTo"] !== null ? values["modificationDateTo"] : null}
                    onChange={field.onChange}
                    renderInput={(params) => <TextField {...params} fullWidth id={"modificationDateFrom"} error={error !== undefined} helperText={error ? error.message : ""}/>}
                    inputFormat={"L"}
            />
    );

    const renderModificationDateTo = (field: any, error: any, state: any, values: any) => (
            <DatePicker
                    views={["day"]}
                    label={t("filters.payments.label-date-to")}
                    value={field.value}
                    minDate={values["modificationDateFrom"] !== null ? values["modificationDateFrom"] : null}
                    onChange={field.onChange}
                    renderInput={(params) => <TextField {...params} fullWidth id={"modificationDateTo"} error={error !== undefined} helperText={error ? error.message : ""}/>}
                    inputFormat={"L"}
            />
    );

    const fields = {
        type: renderType,
        mode: renderMode,
        amountModified: renderAmountModified,
        creationDateFrom: renderCreationDateFrom,
        creationDateTo: renderCreationDateTo,
        modificationDateFrom: renderModificationDateFrom,
        modificationDateTo: renderModificationDateTo,
    };

    const reset = () => {
        setFilters([]);
    };

    const submit = (data: any) => {
        addFilter(setFilters, ID_FILTER_SEARCH, search);
        addFilter(setFilters, ID_FILTER_TYPE, data.type, t("filters.payments.types." + data.type));
        addFilter(setFilters, ID_FILTER_MODE, data.mode, t("filters.payments.modes." + data.mode));
        addFilter(
                setFilters,
                ID_FILTER_AMOUNTMODIFIED,
                data.amountModified === "" ? null : data.amountModified,
                data.amountModified ? t("filters.payments.amount-modified-true") : t("filters.payments.amount-modified-false")
        );
        addFilter(setFilters, ID_FILTER_CREATIONDATEFROM, asStartOfDay(data.creationDateFrom));
        addFilter(setFilters, ID_FILTER_CREATIONDATETO, asEndOfDay(data.creationDateTo));
        addFilter(setFilters, ID_FILTER_MODIFICATIONDATEFROM, asStartOfDay(data.modificationDateFrom));
        addFilter(setFilters, ID_FILTER_MODIFICATIONDATETO, asEndOfDay(data.modificationDateTo));
    };

    return <AdvancedFilter schema={schema} fields={fields} defaultValues={defaultValues} onSubmit={submit} onReset={reset} searchInput={search} setSearchInput={setSearch}/>;
}

export default PaymentsFilter;
